<script setup lang="ts">
/**
 * What the reader pinned — tasks from their drawer,
 * tmux sessions and queued prompts from their bars — one click away in the topbar.
 *
 * A row of pills on a wide screen; on a phone there is no room for them beside
 * the team picker and the switcher, so they fold into a pin button whose menu
 * lists them. Both are rendered and CSS picks one (`.wide-only`/`.narrow-only`
 * would be `inline`, which a flex row of pills is not).
 *
 * Only what the board on screen can show gets a pill (`usePills`): a pinned
 * session is kept on a board that does not code, it just has no pill there.
 */
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import { openPin, usePills, type Pill } from "../lib/pins.js";
import { swallowPress } from "../lib/dismiss.js";
import PinIcon from "./PinIcon.vue";

const board = useBoard();
const viewport = useViewport();
const pills = usePills();

/** A pinned task that was deleted, or moved out of the reader's sight, leads
 *  nowhere: unpinned once the board has loaded, so it does not linger unseen. */
watch(
  () => (board.loaded ? viewport.pins.filter((p) => p.kind === "task" && !board.tasks.has(p.id)).length : 0),
  (gone) => {
    if (gone) viewport.dropPins((p) => p.kind === "task" && !board.tasks.has(p.id));
  },
  { immediate: true },
);

/** What is on screen is one of the pins: the folded button shows it filled. */
const onPinned = computed(() => pills.value.some((p) => p.current));

const waitingInMenu = computed(() => pills.value.some((p) => p.waiting));

const open = ref(false);
const root = ref<HTMLElement | null>(null);

function toggle(): void {
  if (open.value) return close();
  open.value = true;
  window.addEventListener("pointerdown", onPointerDown, true);
  window.addEventListener("keydown", onKey);
}

function close(): void {
  open.value = false;
  window.removeEventListener("pointerdown", onPointerDown, true);
  window.removeEventListener("keydown", onKey);
}

function onPointerDown(ev: PointerEvent): void {
  if (root.value?.contains(ev.target as Node)) return;
  close();
  swallowPress(ev);
}

function onKey(ev: KeyboardEvent): void {
  if (ev.key === "Escape") close();
}

function choose(p: Pill): void {
  openPin(p.pin);
  close();
}

onBeforeUnmount(close);
</script>

<template>
  <template v-if="pills.length">
    <nav class="pinned-pills" aria-label="Pinned">
      <button
        v-for="p in pills"
        :key="p.key"
        class="pinned-pill"
        :class="{ current: p.current, waiting: p.waiting }"
        type="button"
        :title="p.detail ?? (p.kind ? `${p.label} (${p.kind})` : p.label)"
        :aria-current="p.current ? 'page' : undefined"
        @click="choose(p)"
      >
        <span v-if="p.kind" class="pinned-kind">{{ p.kind }}</span>
        <span class="pinned-label">{{ p.label }}</span>
      </button>
    </nav>

    <div ref="root" class="pinned-menu">
      <button
        class="btn ghost icon-btn pinned-menu-btn"
        :class="{ current: onPinned }"
        type="button"
        aria-haspopup="menu"
        :aria-expanded="open"
        title="Pinned"
        aria-label="Pinned"
        @click="toggle"
      >
        <PinIcon :filled="onPinned" />
        <span v-if="waitingInMenu" class="pinned-dot waiting" aria-label="Claude is waiting for an answer"></span>
      </button>
      <ul v-if="open" class="viewmenu-list pinned-menu-list" role="menu">
        <li
          v-for="p in pills"
          :key="p.key"
          role="menuitem"
          :class="{ current: p.current, waiting: p.waiting }"
          :title="p.detail"
          @click="choose(p)"
        >
          <span class="pinned-label">{{ p.label }}</span>
          <span v-if="p.kind" class="pinned-kind">{{ p.kind }}</span>
          </li>
      </ul>
    </div>
  </template>
</template>
